// A date-only value names a calendar day, not an instant, so it renders in UTC. Values carrying a
// time denote a real instant and keep the viewer's own timezone, so a late-evening completion shows
// the day the learner would name.
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/

const ABBREVIATION_LENGTH = 3

/**
 * `30 Jun 2025` in English, `30 Jun. 2025` in German: day first, the month cut to three letters.
 *
 * The day is padded below ten - `05 Nov 2024` - so a column of dates is the same width all the way
 * down.
 *
 * The parts are composed rather than left to one `Intl` pattern, which cannot produce this:
 * `en` orders the month first, `en-GB` abbreviates September to four letters, and German
 * substitutes the wide `Juni` as soon as the month sits in a full date.
 *
 * German dots the abbreviated month, but not `Mai`, which is the whole word, and not the day.
 */
export const shortDateByLocale = (value: string | null | undefined, locale: string | undefined): string | undefined => {
  if (!value) {
    return undefined
  }

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return undefined
  }

  const timeZone = DATE_ONLY.test(value) ? { timeZone: 'UTC' as const } : {}
  const monthName = (width: 'short' | 'long') =>
    new Intl.DateTimeFormat(locale, { month: width, ...timeZone }).format(date)

  const month = monthName('short').slice(0, ABBREVIATION_LENGTH)
  const parts = new Intl.DateTimeFormat('en-GB', { day: '2-digit', year: 'numeric', ...timeZone }).formatToParts(date)
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((candidate) => candidate.type === type)?.value

  if (!locale?.startsWith('de')) {
    return `${part('day')} ${month} ${part('year')}`
  }

  const abbreviated = month !== monthName('long')
  return `${part('day')} ${month}${abbreviated ? '.' : ''} ${part('year')}`
}
